import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Tags, Plus, Pencil, Trash2, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fetchCategories } from "@/lib/api";
import { adminCreateCategory, adminRenameCategory, adminDeleteCategory, formatApiError } from "@/lib/auth";

const inputCls = "bg-ink border border-white/10 focus:border-gold/60 outline-none px-3 py-2 text-sm placeholder:text-slate-600 transition-colors";

export const AdminCategories = () => {
    const queryClient = useQueryClient();
    const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: fetchCategories });
    const [newName, setNewName] = useState("");
    const [creating, setCreating] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState("");
    const [busy, setBusy] = useState(false);

    const refresh = () => queryClient.invalidateQueries({ queryKey: ["categories"] });

    const create = async (e) => {
        e.preventDefault();
        if (!newName.trim()) return;
        setCreating(true);
        try {
            await adminCreateCategory(newName.trim());
            toast.success(`Categoria "${newName.trim()}" criada.`);
            setNewName("");
            refresh();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setCreating(false);
        }
    };

    const startEdit = (cat) => {
        setEditingId(cat.id);
        setEditName(cat.name);
    };

    const saveEdit = async (cat) => {
        if (!editName.trim() || editName.trim() === cat.name) {
            setEditingId(null);
            return;
        }
        setBusy(true);
        try {
            const res = await adminRenameCategory(cat.id, editName.trim());
            toast.success(`Renomeada para "${res.name}" — ${res.articles_updated} artigo(s) atualizados.`);
            setEditingId(null);
            refresh();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (cat) => {
        if (!window.confirm(`Excluir a categoria "${cat.name}"?`)) return;
        setBusy(true);
        try {
            await adminDeleteCategory(cat.id);
            toast.success("Categoria excluída.");
            refresh();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-14">
            <h2 className="font-serif text-2xl tracking-tight mb-6 flex items-center gap-3">
                <Tags size={18} className="text-gold" /> Categorias
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <form data-testid="category-create-form" onSubmit={create} className="border border-white/10 bg-paper p-6 self-start">
                    <label className="block text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Nova categoria</label>
                    <div className="flex gap-2">
                        <input
                            data-testid="category-new-input"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            placeholder="Ex.: Criptomoedas"
                            minLength={2}
                            maxLength={60}
                            className={`${inputCls} flex-1`}
                        />
                        <button
                            data-testid="category-create-btn"
                            type="submit"
                            disabled={creating || newName.trim().length < 2}
                            className="flex items-center gap-1.5 bg-gold text-ink font-medium px-4 py-2 text-sm hover:bg-golddark transition-colors disabled:opacity-50"
                        >
                            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                            Criar
                        </button>
                    </div>
                    <p className="text-[11px] text-slate-600 mt-3 leading-relaxed">
                        Renomear atualiza todos os artigos da categoria automaticamente. Só é possível excluir categorias sem artigos.
                    </p>
                </form>

                <div className="lg:col-span-2 border border-white/10">
                    <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                        <span className="col-span-7">Nome</span>
                        <span className="col-span-2">Artigos</span>
                        <span className="col-span-3 text-right">Ações</span>
                    </div>
                    {categories.map((cat) => (
                        <div key={cat.id} data-testid={`category-row-${cat.id}`} className="grid grid-cols-12 gap-4 px-5 py-3.5 border-b border-white/5 items-center">
                            <div className="col-span-7">
                                {editingId === cat.id ? (
                                    <input
                                        data-testid={`category-edit-input-${cat.id}`}
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter") saveEdit(cat); if (e.key === "Escape") setEditingId(null); }}
                                        autoFocus
                                        className={`${inputCls} w-full`}
                                    />
                                ) : (
                                    <span className="text-sm">{cat.name}</span>
                                )}
                            </div>
                            <span className="col-span-2 text-xs text-slate-500">{cat.count} artigo(s)</span>
                            <div className="col-span-3 flex justify-end gap-2">
                                {editingId === cat.id ? (
                                    <>
                                        <button data-testid={`category-save-${cat.id}`} onClick={() => saveEdit(cat)} disabled={busy} className="p-2 border border-gold/40 text-gold hover:bg-gold hover:text-ink transition-colors" title="Salvar">
                                            <Check size={13} />
                                        </button>
                                        <button data-testid={`category-cancel-${cat.id}`} onClick={() => setEditingId(null)} className="p-2 border border-white/10 text-slate-400 hover:text-white transition-colors" title="Cancelar">
                                            <X size={13} />
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button data-testid={`category-edit-${cat.id}`} onClick={() => startEdit(cat)} className="p-2 border border-white/10 text-slate-400 hover:text-gold hover:border-gold/50 transition-colors" title="Renomear">
                                            <Pencil size={13} />
                                        </button>
                                        <button data-testid={`category-delete-${cat.id}`} onClick={() => remove(cat)} disabled={busy} className="p-2 border border-white/10 text-slate-400 hover:text-red-400 hover:border-red-400/50 transition-colors disabled:opacity-50" title="Excluir">
                                            <Trash2 size={13} />
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
